import { racquetErrorKey } from '../lib/racquetApi'
import { useState } from 'react'
import {
  reconstructRacquet,
  activeRacquetEvents
} from '../../../shared/racquetScoreDomain.mjs'
export default function RacquetCorrection({
  match,
  t,
  onPreview,
  onClose,
  viewId
}) {
  const s = match.state,
    racket = ['tennis', 'padel'].includes(match.rules.sport)
  const [mode, setMode] = useState(
      ['completed', 'awaiting_confirmation'].includes(s.status)
        ? 'replace_segment'
        : 'correct_current'
    ),
    [scores, setScores] = useState([...s.points]),
    [server, setServer] = useState(racket ? s.server : ''),
    [box, setBox] = useState(''),
    [number, setNumber] = useState(''),
    [positions, setPositions] = useState(
      s.teams.map((team) => team.players[0])
    ),
    [team, setTeam] = useState(s.teams[0].id),
    [reason, setReason] = useState(''),
    [index, setIndex] = useState(0),
    [discard, setDiscard] = useState(false),
    [error, setError] = useState(false)
  const build = () => {
    if (!reason.trim())
      throw Object.assign(new Error(), { code: 'reason_required' })
    if (mode === 'replace_last_rally')
      return {
        type: mode,
        targetEventId: activeRacquetEvents(match.events).at(-1)?.id,
        teamId: team,
        reason: reason.trim()
      }
    if (mode === 'award_game')
      return { type: mode, teamId: team, reason: reason.trim() }
    if (mode === 'correct_games')
      return { type: mode, games: scores, server, reason: reason.trim() }
    if (mode !== 'correct_current')
      return {
        type: mode,
        score: scores,
        server,
        serviceBox: box,
        reason: reason.trim(),
        ...(mode === 'replace_segment'
          ? { index, discardFollowing: discard }
          : {})
      }
    return {
      type: mode,
      points: scores,
      server,
      serviceBox: racket
        ? (scores[0] + scores[1]) % 2 === 0
          ? 'right'
          : 'left'
        : box,
      reason: reason.trim(),
      ...(match.rules.sport === 'pickleball'
        ? {
            serverNumber: Number(number),
            positions: s.teams.map((team, i) => [
              positions[i],
              team.players.find((id) => id !== positions[i]) || null
            ])
          }
        : {})
    }
  }
  return (
    <section
      className="rp-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={t('correct')}
    >
      <h2>{t('correct')}</h2>
      <button onClick={onClose}>{t('cancel')}</button>
      <p>{t('summaryNote')}</p>
      <label>
        {t('correct')}
        <select
          aria-label={t('correct')}
          value={mode}
          onChange={(e) => {
            setMode(e.target.value)
            setScores([0, 0])
            setServer(
              e.target.value === 'correct_current' && racket && !s.tieBreak
                ? s.server
                : ''
            )
            setBox('')
          }}
        >
          {s.status === 'in_progress' && (
            <>
              <option value="correct_current">{t('current')}</option>
              {activeRacquetEvents(match.events).at(-1)?.operation.type ===
                'rally' && (
                <option value="replace_last_rally">
                  {t('correctLastRally')}
                </option>
              )}
              {racket && !s.tieBreak && (
                <option value="award_game">{t('awardGame')}</option>
              )}
              <option value="summary_segment">{t('summary')}</option>
              {racket && (
                <option value="correct_games">{t('currentGames')}</option>
              )}
            </>
          )}
          {s.segments.length > 0 && (
            <option value="replace_segment">{t('historical')}</option>
          )}
        </select>
      </label>
      {['award_game', 'replace_last_rally'].includes(mode) ? (
        <label>
          {t('team')}
          <select value={team} onChange={(e) => setTeam(e.target.value)}>
            {s.teams.map((team, i) => (
              <option value={team.id} key={team.id}>
                {t('team')} {i + 1}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <>
          {mode === 'replace_segment' && (
            <>
              <label>
                {t('segment')}
                <select
                  value={index}
                  onChange={(e) => setIndex(Number(e.target.value))}
                >
                  {s.segments.map((seg, i) => (
                    <option value={i} key={i}>
                      {i + 1}: {seg.score.join('–')}
                    </option>
                  ))}
                </select>
              </label>
              <label className="rp-check">
                <input
                  type="checkbox"
                  checked={discard}
                  onChange={(e) => setDiscard(e.target.checked)}
                />
                {t('discardFollowing')}
              </label>
            </>
          )}
          <div className="rp-team-grid">
            {scores.map((value, i) => (
              <label key={i}>
                {t('team')} {i + 1} · {t('points')}
                {racket && mode === 'correct_current' && !s.tieBreak ? (
                  <select
                    value={value}
                    onChange={(e) =>
                      setScores((v) =>
                        v.map((n, j) => (j === i ? Number(e.target.value) : n))
                      )
                    }
                  >
                    {['0', '15', '30', '40', 'AD'].map((label, n) => (
                      <option value={n} key={n}>
                        {label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    inputMode="numeric"
                    type="number"
                    min={0}
                    max={1000}
                    value={value}
                    onChange={(e) =>
                      setScores((v) =>
                        v.map((n, j) => (j === i ? Number(e.target.value) : n))
                      )
                    }
                  />
                )}
              </label>
            ))}
          </div>
          <label>
            {t('server')}
            <select
              aria-label={t('server')}
              value={server}
              onChange={(e) => setServer(e.target.value)}
              disabled={racket && mode === 'correct_current' && !s.tieBreak}
            >
              <option value="">{t('unknown')}</option>
              {match.participants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </label>
          {!(racket && ['correct_current', 'correct_games'].includes(mode)) && (
            <label>
              {t('initialBox')}
              <select
                aria-label={t('initialBox')}
                value={box}
                onChange={(e) => setBox(e.target.value)}
              >
                <option value="">{t('unknown')}</option>
                <option value="right">{t('right')}</option>
                <option value="left">{t('left')}</option>
              </select>
            </label>
          )}
          {match.rules.sport === 'pickleball' && mode === 'correct_current' && (
            <>
              <label>
                {t('serverNumber')}
                <select
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                >
                  <option value="">{t('unknown')}</option>
                  <option value="1">1</option>
                  {s.teams[0].players.length === 2 && (
                    <option value="2">2</option>
                  )}
                </select>
              </label>
              {s.teams[0].players.length === 2 &&
                s.teams.map((team, i) => (
                  <label key={team.id}>
                    {t('team')} {i + 1} · {t('positions')}
                    <select
                      value={positions[i]}
                      onChange={(e) =>
                        setPositions((ps) =>
                          ps.map((p, j) => (j === i ? e.target.value : p))
                        )
                      }
                    >
                      {team.players.map((id) => (
                        <option value={id} key={id}>
                          {
                            match.participants.find((p) => p.id === id)
                              ?.display_name
                          }
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
            </>
          )}
        </>
      )}
      <label>
        {t('reason')}
        <input
          maxLength={300}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {error && <p role="alert">{t(racquetErrorKey(error))}</p>}
      <button
        className="rp-primary"
        onClick={() => {
          try {
            const operation = build(),
              after = reconstructRacquet(match.initialState, [
                ...match.events,
                { id: 'preview', operation }
              ])
            onPreview({
              operation,
              after,
              before: s,
              revision: match.revision,
              viewId
            })
          } catch (error) {
            setError(error.code || 'error')
          }
        }}
      >
        {t('preview')}
      </button>
    </section>
  )
}
